import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import {
  api,
  useGetGoogleLoginQuery,
  useLoginCreateMutation,
  usePostClaimSurveyMutation,
  usePostPasswordResetMutation,
  usePostResendOtpMutation,
  usePostVerify2FaMutation,
  usePostVerifyGoogleAuthMutation,
  useSignupCreateMutation,
} from "../store/api.ts";
import { getFirstError, getPendingSurveyToken, removePendingSurveyToken } from "../utils.js";
import {
  logSignupFailure,
  logSignupStart,
  logSignupSuccess,
} from "../utils/analytics";
import { ReactComponent as ModalLogo } from "./../assets/svgs/modal-logo.svg";

const inputClass =
  "h-[40px] w-full rounded-[5px] border border-[#D9D5D0] font-medium bg-white px-4 text-[14px] text-[#8A8580] outline-none placeholder:text-[#A0A0A0] focus:border-[#313131]";

const socialButtonClass =
  "flex h-[40px] w-full items-center justify-center gap-3 rounded-[5px] border border-[#D9D5D0] bg-white text-[12px] font-medium text-[#313131] transition-colors hover:bg-[#F8F8F8] disabled:cursor-not-allowed disabled:opacity-60";

const handledGoogleCallbacks = new Set();

const isProfileMissingError = (error) => {
  const detail =
    error?.data?.detail ||
    error?.data?.error ||
    error?.error ||
    error?.message ||
    "";

  const normalizedDetail = String(detail).toLowerCase();

  return (
    error?.status === 404 ||
    normalizedDetail.includes("no profile") ||
    normalizedDetail.includes("profile not found")
  );
};

const AuthModal = ({
  isOpen,
  initialMode = "login",
  autoStartGoogle = false,
  preloadGoogle = false,
  onGoogleStartConsumed,
  onClose,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const authReturnTo = useMemo(() => {
    const isProductReturnPage = location.pathname === "/product" || location.pathname.startsWith("/product/");
    const isCartReturnPage = location.pathname === "/cart";
    if (!isProductReturnPage && !isCartReturnPage) {
      return "/";
    }
    const params = new URLSearchParams(location.search);
    params.delete("google_auth_key");
    params.delete("error");
    const cleanedSearch = params.toString();
    return [
      location.pathname,
      cleanedSearch ? `?${cleanedSearch}` : "",
      location.hash || "",
    ].join("");
  }, [
    location.pathname,
    location.search,
    location.hash,
  ]);
  const [searchParams, setSearchParams] = useSearchParams();
  const googleAuthKey = searchParams.get("google_auth_key");
  const googleOauthError = searchParams.get("error");

  const [mode, setMode] = useState(initialMode);
  const googleCallbackHandledRef = useRef(null);
  const autoGoogleStartedRef = useRef(false);

  const [login, { isLoading: isLoginLoading }] = useLoginCreateMutation();
  const [signup, { isLoading: isSignupLoading }] = useSignupCreateMutation();
  const [
    verifyGoogleAuth,
    { isLoading: isGoogleVerificationLoading },
  ] = usePostVerifyGoogleAuthMutation();

  const [isCompletingLogin, setIsCompletingLogin] = useState(false);
  const [isCompletingGoogleLogin, setIsCompletingGoogleLogin] = useState(false);
  const [isClaimingSurvey, setIsClaimingSurvey] = useState(false);

  const [
    claimSurvey,
    { isLoading: isClaimSurveyMutationLoading },
  ] = usePostClaimSurveyMutation();
  const claimSurveyPromiseRef = useRef(null);
  const [verifyEmail] = usePostVerify2FaMutation();
  const [resendOtp] = usePostResendOtpMutation();
  const [sendResetLink, { isLoading: isPasswordResetLoading }] =
    usePostPasswordResetMutation();
  const [verificationEmail, setVerificationEmail] = useState("");

  const getInitialLoginForm = () => ({
    email: "",
    password: "",
  });

  const getInitialSignupForm = () => ({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirm_password: "",
  });

  const getInitialForgotPasswordForm = () => ({
    email: "",
  });

  const getInitialVerificationCode = () => ["", "", "", "", "", ""];

  const resetAuthModalState = () => {
    setMode(initialMode);
    setLoginForm(getInitialLoginForm());
    setSignupForm(getInitialSignupForm());
    setForgotPasswordForm(getInitialForgotPasswordForm());
    setVerificationEmail("");
    setVerificationCode(getInitialVerificationCode());
    setVerifyLoading(false);
    setTimeLeft(60);
    setResending(false);
  };

  const handleCloseModal = () => {
    resetAuthModalState();
    onClose?.();
  };

  const [verificationCode, setVerificationCode] = useState(
    getInitialVerificationCode,
  );
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const [resending, setResending] = useState(false);

  const [forgotPasswordForm, setForgotPasswordForm] = useState(
    getInitialForgotPasswordForm,
  );

  const dispatch = useDispatch();
  const shouldLoadGoogleAuth = isOpen || preloadGoogle;
  const {
    data: googleLoginData,
    isLoading: googleAuthLoading,
    isFetching: isGoogleAuthFetching,
    error: googleAuthError,
    isError: isGoogleAuthError,
    refetch: refetchGoogleLogin,
  } = useGetGoogleLoginQuery(undefined, {
    skip: !shouldLoadGoogleAuth,
    refetchOnMountOrArgChange: false,
  });

  const [loginForm, setLoginForm] = useState(getInitialLoginForm);

  const [signupForm, setSignupForm] = useState(getInitialSignupForm);

  useEffect(() => {
    if (!isOpen) return;

  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
    }
  }, [isOpen, initialMode]);

  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event) => {
      if (
        event.key === "Escape" &&
        !isCompletingLogin &&
        !isCompletingGoogleLogin &&
        !isClaimingSurvey &&
        !isClaimSurveyMutationLoading
      ) {
        resetAuthModalState();
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [
    isOpen,
    onClose,
    isCompletingLogin,
    isCompletingGoogleLogin,
    isClaimingSurvey,
    isClaimSurveyMutationLoading,
  ]);

  const fetchCurrentProfile = useCallback(async () => {
    const profileRequest = dispatch(
      api.endpoints.profileRead.initiate(undefined, {
        forceRefetch: true,
      }),
    );

    try {
      return await profileRequest.unwrap();
    } finally {
      profileRequest.unsubscribe?.();
    }
  }, [dispatch]);

  const saveAuthenticatedUser = useCallback((response, options = {}) => {
    const { notify = true } = options;
    if (response?.token) {
      localStorage.setItem("authToken", response.token);
    }

    if (response?.user) {
      localStorage.setItem("authUser", JSON.stringify(response.user));
    }

    if (notify) {
      window.dispatchEvent(new Event("eveness-auth-changed"));
    }
  }, []);

  const claimPendingSurvey = useCallback(
    async (pendingToken) => {
      if (!pendingToken) {
        return null;
      }

      if (claimSurveyPromiseRef.current) {
        return claimSurveyPromiseRef.current;
      }
      setIsClaimingSurvey(true);

      const claimRequest = (async () => {
        const claimedProfile = await claimSurvey({
          claimSurvey: {
            token: pendingToken,
          },
        }).unwrap();

        removePendingSurveyToken();

        try {
          sessionStorage.removeItem("personal_fashion_engine_signup");
        } catch {}

        return claimedProfile;
      })();

      claimSurveyPromiseRef.current = claimRequest;

      try {
        return await claimRequest;
      } finally {
        claimSurveyPromiseRef.current = null;
        setIsClaimingSurvey(false);
      }
    },
    [claimSurvey],
  );

  const loadProfileAfterLogin = useCallback(async () => {
    const pendingToken = getPendingSurveyToken();

    let claimFailed = false;
    let surveyClaimed = false;

    if (pendingToken) {
      try {
        await claimPendingSurvey(pendingToken);
        surveyClaimed = true;
      } catch (claimError) {
        claimFailed = true;

        console.error("Login succeeded, but claim-survey failed:", claimError);

        toast.error(
          getFirstError(claimError?.data || claimError) ||
            "Login succeeded, but your saved profile could not be attached.",
        );
      }
    }

    try {
      const profile = await fetchCurrentProfile();

      return {
        profile,
        profileMissing: false,
        claimFailed,
        surveyClaimed,
      };
    } catch (profileError) {
      if (isProfileMissingError(profileError)) {
        return {
          profile: null,
          profileMissing: true,
          claimFailed,
          surveyClaimed,
        };
      }

      console.error(
        "Login succeeded, but profile loading failed:",
        profileError,
      );

      throw profileError;
    }
  }, [claimPendingSurvey, fetchCurrentProfile]);

  useEffect(() => {
    const callbackId = googleAuthKey
      ? `google-auth:${googleAuthKey}`
      : googleOauthError
        ? `google-error:${googleOauthError}`
        : null;

    if (!callbackId) {
      return;
    }

    if (googleCallbackHandledRef.current === callbackId) {
      return;
    }
    if (googleAuthKey && handledGoogleCallbacks.has(callbackId)) {
      return;
    }
    googleCallbackHandledRef.current = callbackId;
    if (googleAuthKey) {
      handledGoogleCallbacks.add(callbackId);
    }
    setSearchParams(
      (currentParams) => {
        const nextParams = new URLSearchParams(currentParams);

        nextParams.delete("google_auth_key");
        nextParams.delete("error");

        return nextParams;
      },
      {
        replace: true,
      },
    );
    try {
      sessionStorage.removeItem("eveness_google_auth_in_progress");
    } catch {}

    if (googleOauthError) {
      toast.error("Google login failed");
      logSignupFailure("google_oauth_error", "google");
      return;
    }

    const verifyAuth = async () => {
      if (isCompletingGoogleLogin) return;
      setIsCompletingGoogleLogin(true);

      try {
        const response = await verifyGoogleAuth({
          verifyGoogleAuth: {
            google_auth_key: googleAuthKey,
          },
        }).unwrap();
        saveAuthenticatedUser(response, {
          notify: false,
        });
        const {
          profile,
          profileMissing,
          claimFailed,
          surveyClaimed,
        } = await loadProfileAfterLogin();

        if (claimFailed) {
          return;
        }

        window.dispatchEvent(
          new Event("eveness-auth-changed"),
        );

        const shouldShowPersonalization =
          !surveyClaimed &&
          profileMissing &&
          !getPendingSurveyToken();

        toast.success(
          response?.message || "Login successful",
        );

        if (surveyClaimed) {
          toast.success("Profile updated");
        }

        logSignupSuccess("google");

        window.dispatchEvent(
          new CustomEvent("eveness-login-completed", {
            detail: {
              user: response?.user || null,
              profile,
              profileMissing,
              claimFailed: false,
              surveyClaimed,
              shouldShowPersonalization,
            },
          }),
        );

        const hasCompletedProfile =
          !profileMissing &&
          profile?.face_shape &&
          profile?.body_type &&
          profile?.seasonal_color;

        handleCloseModal();
        onGoogleStartConsumed?.();

        if (hasCompletedProfile || surveyClaimed) {
          navigate(authReturnTo, { replace: true });
          return;
        }

      } catch (err) {
        logSignupFailure(
          err?.status || "google_verify_error",
          "google",
        );

        toast.error(
          getFirstError(err?.data || err) ||
          "Google login failed",
        );
      } finally {
        setIsCompletingGoogleLogin(false);
      }
    };

    verifyAuth();
  }, [
    googleAuthKey,
    googleOauthError,
    setSearchParams,
    verifyGoogleAuth,
    saveAuthenticatedUser,
    loadProfileAfterLogin,
    navigate,
    onClose,
    authReturnTo
  ]);

  useEffect(() => {
    if (mode !== "verifyEmail") return;
    if (timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [mode, timeLeft]);

  const handleLoginInputChange = (event) => {
    const { name, value } = event.target;

    setLoginForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSignupInputChange = (event) => {
    const { name, value } = event.target;

    setSignupForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;

    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const resetVerificationState = (email) => {
    setVerificationEmail(email);
    setVerificationCode(["", "", "", "", "", ""]);
    setTimeLeft(60);
    setMode("verifyEmail");
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();

    if (isCompletingLogin) return;
    setIsCompletingLogin(true);

    try {
      const response = await login({ login: loginForm }).unwrap();

      saveAuthenticatedUser(response, {
        notify: false,
      });
      const {
        profile,
        profileMissing,
        claimFailed,
        surveyClaimed,
      } = await loadProfileAfterLogin();
      if (claimFailed) {
        return;
      }

      window.dispatchEvent(new Event("eveness-auth-changed"));

      const shouldShowPersonalization =
        !surveyClaimed &&
        profileMissing &&
        !getPendingSurveyToken();

      window.dispatchEvent(
        new CustomEvent("eveness-login-completed", {
          detail: {
            user: response?.user || null,
            profile,
            profileMissing,
            claimFailed: false,
            surveyClaimed,
            shouldShowPersonalization,
          },
        }),
      );

      toast.success(
        response?.message || "Login successful",
      );

      if (surveyClaimed) {
        toast.success("Profile updated");
      }

      const hasCompletedProfile =
        !profileMissing &&
        profile?.face_shape &&
        profile?.body_type &&
        profile?.seasonal_color;

      handleCloseModal();

      if (hasCompletedProfile || surveyClaimed) {
        navigate(authReturnTo, { replace: true });
        return;
      }

    } catch (err) {
      if (err?.data?.requires_verification) {
        toast.error(err?.data?.error || "Please verify your account first.");
        resetVerificationState(loginForm.email);
        return;
      }

      toast.error(getFirstError(err?.data || err) || "Something went wrong while logging in.");
    } finally {
      setIsCompletingLogin(false);
    }
  };

  const handleCodeChange = (index, value) => {
    if (value && !/^\d+$/.test(value)) return;

    const nextCode = [...verificationCode];
    nextCode[index] = value.slice(-1);
    setVerificationCode(nextCode);

    if (value && index < 5) {
      document.getElementById(`modal-code-input-${index + 1}`)?.focus();
    } else if (!value && index > 0) {
      document.getElementById(`modal-code-input-${index - 1}`)?.focus();
    }
  };

  const handleCodePaste = (event) => {
    event.preventDefault();

    const paste = event.clipboardData.getData("text").trim();

    if (!/^\d{6}$/.test(paste)) {
      toast.error("Please paste a 6-digit numeric code.");
      return;
    }

    setVerificationCode(paste.split(""));
    document.getElementById("modal-code-input-5")?.focus();
  };

  const handleResendCode = async () => {
    if (!verificationEmail) {
      toast.error("Email is missing. Please sign up again.");
      setMode("signup");
      return;
    }

    setResending(true);

    try {
      const response = await resendOtp({
        resendOtp: { email: verificationEmail },
      }).unwrap();

      toast.success(
        response?.message || "A new verification code has been sent!",
      );
      setTimeLeft(60);
    } catch (err) {
      toast.error(
        Array.isArray(err?.data?.email) && err.data.email.length > 0
          ? err.data.email[0]
          : "Failed to resend code. Try again later.",
      );
    } finally {
      setResending(false);
    }
  };

  const handleVerifyEmailSubmit = async (event) => {
    event.preventDefault();

    const code = verificationCode.join("");

    if (code.length !== 6) {
      toast.error("Please enter all 6 digits of the verification code");
      return;
    }

    if (!verificationEmail) {
      toast.error("Email is missing. Please sign up again.");
      setMode("signup");
      return;
    }

    setVerifyLoading(true);

    try {
      await verifyEmail({
        verify2Fa: {
          email: verificationEmail,
          pin: code,
        },
      }).unwrap();

      toast.success("Email verified successfully!");
      setMode("login");
      setLoginForm((prev) => ({
        ...prev,
        email: verificationEmail,
      }));
    } catch (err) {
      toast.error(
        err?.data?.message || "Invalid verification code. Please try again.",
      );
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleSignupSubmit = async (event) => {
    event.preventDefault();

    if (signupForm.password !== signupForm.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }

    const payload = {
      first_name: signupForm.first_name,
      last_name: signupForm.last_name,
      email: signupForm.email,
      password: signupForm.password,
    };

    logSignupStart("email");

    try {
      await signup({ signUp: payload }).unwrap();

      logSignupSuccess("email");
      toast.success("Account created! Please verify your email.");
      resetVerificationState(signupForm.email);
    } catch (err) {
      console.log("ERROR 1:", err);
      const errorMessage = getFirstError(err?.data || "Something went wrong");
      logSignupFailure(err?.status || "api_error", "email");
      toast.error(errorMessage);
    }
  };

  const handleGoogleLogin = useCallback(() => {
    if (googleAuthLoading || isGoogleAuthFetching) return false;

    if (
      isGoogleAuthError ||
      !googleLoginData?.success ||
      !googleLoginData?.auth_url
    ) {
      toast.error(
        getFirstError(googleAuthError?.data || googleAuthError) ||
          "Google login failed",
      );

      return false;
    }

    try {
      sessionStorage.setItem("eveness_google_auth_in_progress", "true");
    } catch {}

    logSignupStart("google");
    window.location.assign(googleLoginData.auth_url);
    return true;
  }, [
    googleAuthLoading,
    isGoogleAuthFetching,
    isGoogleAuthError,
    googleLoginData,
    googleAuthError,
  ]);

  useEffect(() => {
    if (!isOpen || !autoStartGoogle) {
      autoGoogleStartedRef.current = false;
      return;
    }
    if (autoGoogleStartedRef.current) {
      return;
    }
    if (googleAuthLoading || isGoogleAuthFetching) {
      return;
    }
    if (!googleLoginData && !isGoogleAuthError) {
      return;
    }
    autoGoogleStartedRef.current = true;
    const didStartRedirect = handleGoogleLogin();
    if (!didStartRedirect) {
      autoGoogleStartedRef.current = false;
      onGoogleStartConsumed?.();
      onClose?.();
    }
  }, [
    isOpen,
    autoStartGoogle,
    googleAuthLoading,
    isGoogleAuthFetching,
    googleLoginData,
    isGoogleAuthError,
    handleGoogleLogin,
    onGoogleStartConsumed,
    onClose,
  ]);

  useEffect(() => {
    const resetCancelledGoogleLogin = () => {

      const currentParams = new URLSearchParams(window.location.search);

      const hasGoogleCallback =
        currentParams.has("google_auth_key") || currentParams.has("error");

      if (hasGoogleCallback) {
        return;
      }

      let googleLoginWasStarted = false;

      try {
        googleLoginWasStarted =
          sessionStorage.getItem("eveness_google_auth_in_progress") === "true";
      } catch {}

      if (!googleLoginWasStarted) {
        return;
      }

      try {
        sessionStorage.removeItem("eveness_google_auth_in_progress");
      } catch {}
      autoGoogleStartedRef.current = false;
      onGoogleStartConsumed?.();
      onClose?.();
      if (shouldLoadGoogleAuth) {
        refetchGoogleLogin();
      }
    };

    const handlePageShow = () => {
      resetCancelledGoogleLogin();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        resetCancelledGoogleLogin();
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [
    onGoogleStartConsumed,
    onClose,
    shouldLoadGoogleAuth,
    refetchGoogleLogin,
  ]);

  const isUpdatingProfile =
    isClaimingSurvey ||
    isClaimSurveyMutationLoading;

  const isLoginFlowLoading =
    isLoginLoading ||
    isCompletingLogin ||
    isUpdatingProfile;

  const isGoogleLoginFlowLoading =
    googleAuthLoading ||
    isGoogleAuthFetching ||
    isGoogleVerificationLoading ||
    isCompletingGoogleLogin ||
    isUpdatingProfile;

  const isAuthenticationFlowLoading =
    isLoginFlowLoading ||
    isGoogleLoginFlowLoading;

  const renderLogin = () => (
    <form onSubmit={handleLoginSubmit} className="mx-auto w-full max-w-[292px]">
      <div className="flex flex-col items-center gap-[20px]">
        <button className="flex h-[52px] w-[50px] items-center justify-center rounded-[5px] bg-gradient-to-br from-[#F5C5B8] via-[#E1A39B] to-[#A84447] shadow-sm transition hover:opacity-90 cursor-default">              
          <ModalLogo />
        </button>
        <h2 className="text-center font-helvetica text-[28px] font-semibold leading-none text-black">
          Log in to continue
        </h2>
      </div>
      <p className="mx-auto mt-4 max-w-[280px] text-center font-helvetica text-[14px] font-normal leading-[18px] text-[#8A8580]">
        Get personalized match scores, fit recommendations, and styling advice
        tailored to your body and preferences.
      </p>

      <div className="mt-8 flex flex-col gap-3">
        <input
          type="email"
          name="email"
          placeholder="Email Address"
          value={loginForm.email}
          onChange={handleLoginInputChange}
          className={inputClass}
          required
        />

        <input
          type="password"
          name="password"
          placeholder="Confirm Password"
          value={loginForm.password}
          onChange={handleLoginInputChange}
          className={inputClass}
          required
        />

        <button
          type="button"
          onClick={() => {
            setForgotPasswordForm({ email: loginForm.email });
            setMode("forgotPassword");
          }}
          className="self-end text-[12px] font-medium text-[#777] hover:text-[#313131]"
        >
          Forgot Password?
        </button>

        <button
          type="submit"
          disabled={isAuthenticationFlowLoading}
          className="flex h-[40px] items-center justify-center gap-2 rounded-[5px] bg-[#313131] text-[12px] font-medium text-white transition-colors hover:bg-[#1F1F23] disabled:cursor-not-allowed disabled:bg-gray-400"
        >
          {isLoginFlowLoading && (
            <span
              className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
              aria-hidden="true"
            />
          )}

          {isUpdatingProfile
            ? "Updating profile..."
            : isLoginFlowLoading
              ? "Logging in..."
              : "Log in"}
        </button>

        {isUpdatingProfile && (
          <div
            className="flex items-start gap-2 rounded-[5px] border border-[#E7E1DB] bg-[#FAF8F6] px-3 py-3"
            role="status"
            aria-live="polite"
          >
            <span
              className="mt-[2px] h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-[#C7B9AF] border-t-[#313131]"
              aria-hidden="true"
            />

            <div>
              <p className="text-[12px] font-medium text-[#313131]">
                Updating your profile
              </p>

              <p className="mt-1 text-[11px] leading-[16px] text-[#8A8580]">
                Please wait while we attach your survey results to your account.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="my-4 flex items-center gap-3">
        <span className="h-px flex-1 bg-[#E5E5E5]" />
        <span className="text-[11px] text-[#A0A0A0]">or</span>
        <span className="h-px flex-1 bg-[#E5E5E5]" />
      </div>

      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={handleGoogleLogin}
          disabled={isAuthenticationFlowLoading}
          className={socialButtonClass}
        >
          {isGoogleLoginFlowLoading ? (
            <>
              <span
                className="h-4 w-4 animate-spin rounded-full border-2 border-[#BDBDBD] border-t-[#313131]"
                aria-hidden="true"
              />
              <span>
                {isUpdatingProfile
                  ? "Updating profile..."
                  : "Signing in with Google..."}
              </span>
            </>
          ) : (
            <>
              <span className="font-bold text-[#4285F4]">
                G
              </span>
              <span>Continue with Google</span>
            </>
          )}
        </button>
      </div>

      <p className="mt-10 text-center text-[12px] text-[#9B9B9B]">
        Don&apos;t have an account?{" "}
        <button
          type="button"
          onClick={() => setMode("signup")}
          className="font-medium text-[#313131]"
        >
          Register now
        </button>
      </p>
    </form>
  );

  const renderSignup = () => (
    <form
      onSubmit={handleSignupSubmit}
      className="mx-auto w-full max-w-[400px]"
    >
      <div className="flex flex-col items-center gap-[20px]">
        <button className="flex h-[52px] w-[50px] items-center justify-center rounded-[5px] bg-gradient-to-br from-[#F5C5B8] via-[#E1A39B] to-[#A84447] shadow-sm transition hover:opacity-90 cursor-default">
          <ModalLogo />
        </button>
        <h2 className="text-center font-helvena text-[24px] font-medium leading-none text-[#313131]">
          Create a new Account
        </h2>
      </div>

      <p className="mx-auto mt-4 text-center font-helvena text-[12px] leading-[20px] text-[#8A8580]">
        Get personalized match scores, fit recommendations, and styling advice
        tailored to your body and preferences.
      </p>

      <div className="mt-5 flex flex-col gap-3">
        <input
          type="text"
          name="first_name"
          placeholder="First Name"
          value={signupForm.first_name}
          onChange={handleSignupInputChange}
          className={inputClass}
          required
        />

        <input
          type="text"
          name="last_name"
          placeholder="Last Name"
          value={signupForm.last_name}
          onChange={handleSignupInputChange}
          className={inputClass}
          required
        />

        <input
          type="email"
          name="email"
          placeholder="Email Address"
          value={signupForm.email}
          onChange={handleSignupInputChange}
          className={inputClass}
          required
        />

        <input
          type="password"
          name="password"
          placeholder="Create Password"
          value={signupForm.password}
          onChange={handleSignupInputChange}
          className={inputClass}
          pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$"
          title="Password must contain at least 8 characters, one uppercase, one lowercase, one number and one special character"
          required
        />

        <input
          type="password"
          name="confirm_password"
          placeholder="Confirm Password"
          value={signupForm.confirm_password}
          onChange={handleSignupInputChange}
          className={inputClass}
          required
        />

        <button
          type="submit"
          disabled={isSignupLoading}
          className="flex h-[42px] max-w-[415px] items-center justify-center rounded-[5px] bg-gradient-to-r from-[#E9AEA1] via-[#DF9188] to-[#B76570] px-[92px] font-helvena text-[14px] font-normal text-white shadow-sm transition hover:brightness-[0.98] disabled:cursor-not-allowed disabled:opacity-55"
        >
          {isSignupLoading ? "Creating Account..." : "Create Account"}
        </button>
      </div>

      <div className="my-4 flex items-center gap-3 flex-row justify-center">
        <span className="text-[12px] text-[#8A8580] font-helvena">Or</span>
      </div>

      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={handleGoogleLogin}
          disabled={isAuthenticationFlowLoading}
          className={socialButtonClass}
        >
          {isGoogleLoginFlowLoading ? (
            <>
              <span
                className="h-4 w-4 animate-spin rounded-full border-2 border-[#BDBDBD] border-t-[#313131]"
                aria-hidden="true"
              />

              <span>
                {isUpdatingProfile
                  ? "Updating profile..."
                  : "Signing in with Google..."}
              </span>
            </>
          ) : (
            <>
              <span className="font-bold text-[#4285F4]">
                G
              </span>

              <span>Continue with Google</span>
            </>
          )}
        </button>
      </div>

      <p className="mt-[22px] text-center font-helvena text-[12px] text-[#8A8580] whitespace-nowrap">
        By continuing you are accepting eveness's terms and conditions,{" "}
        <button
          type="button"
          onClick={() => navigate("/policies/terms-of-service")}
          className="font-semibold text-[#282828] hover:underline"
        >
          read more
        </button>
      </p>
      <p className="mt-[10px] text-center font-helvena text-[12px] text-[#8A8580] flex flex-row justify-center">
        No credit card required&nbsp;&nbsp;•&nbsp;&nbsp;Already have an
        account?&nbsp;
        <button
          type="button"
          onClick={() => setMode("login")}
          className="font-semibold text-[#282828] hover:underline"
        >
          Sign In.
        </button>
      </p>
    </form>
  );

  const renderVerifyEmail = () => (
    <form
      onSubmit={handleVerifyEmailSubmit}
      className="mx-auto w-full max-w-[360px]"
    >
      <div className="flex flex-col items-center gap-[20px]">
        <button className="flex h-[52px] w-[50px] items-center justify-center rounded-[5px] bg-gradient-to-br from-[#F5C5B8] via-[#E1A39B] to-[#A84447] shadow-sm transition hover:opacity-90 cursor-default">
          <ModalLogo />
        </button>
        <h2 className="text-center font-helvetica text-[28px] font-bold leading-none text-[#111111]">
          Verify your email
        </h2>
      </div>

      <p className="mx-auto mt-4 max-w-[320px] text-center font-helvetica text-[14px] leading-[18px] text-[#9B9B9B]">
        We&apos;ve sent a 6-digit verification code to
        <br />
        <span className="font-medium text-[#313131]">
          {verificationEmail || "your email address"}
        </span>
      </p>

      <div className="mt-8 flex justify-center gap-2">
        {verificationCode.map((digit, index) => (
          <input
            key={index}
            id={`modal-code-input-${index}`}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(event) => handleCodeChange(index, event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Backspace" && !digit && index > 0) {
                document
                  .getElementById(`modal-code-input-${index - 1}`)
                  ?.focus();
              }
            }}
            onPaste={handleCodePaste}
            className="h-[48px] w-[42px] rounded-[4px] border border-[#D8D8D8] bg-white text-center text-[18px] font-bold text-[#313131] outline-none focus:border-[#313131] md:h-[56px] md:w-[50px]"
            autoFocus={index === 0}
          />
        ))}
      </div>

      <button
        type="submit"
        disabled={verifyLoading}
        className="mt-8 h-[40px] w-full rounded-[5px] bg-[#313131] text-[12px] font-medium text-white transition-colors hover:bg-[#1F1F23] disabled:cursor-not-allowed disabled:bg-gray-400"
      >
        {verifyLoading ? "Verifying..." : "Verify Email"}
      </button>

      <div className="mt-8 text-center">
        {timeLeft > 0 ? (
          <p className="text-[14px] text-[#9B9B9B]">
            You can resend the code in{" "}
            <span className="font-medium text-[#313131]">
              {formatTime(timeLeft)}
            </span>
          </p>
        ) : (
          <button
            type="button"
            onClick={handleResendCode}
            disabled={resending}
            className="text-[12px] font-medium text-[#313131] underline disabled:text-gray-400"
          >
            {resending ? "Resending..." : "Resend verification code"}
          </button>
        )}
      </div>

      <p className="mt-6 text-center text-[12px] text-[#9B9B9B]">
        Wrong email?{" "}
        <button
          type="button"
          onClick={() => setMode("signup")}
          className="font-medium text-[#313131]"
        >
          Go back
        </button>
      </p>
    </form>
  );

  const handleForgotPasswordInputChange = (event) => {
    const { name, value } = event.target;

    setForgotPasswordForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleForgotPasswordSubmit = async (event) => {
    event.preventDefault();

    try {
      await sendResetLink({
        passwordResetRequest: forgotPasswordForm,
      }).unwrap();

      toast.success("Password reset link sent to your email");
      setMode("login");
      setLoginForm((prev) => ({
        ...prev,
        email: forgotPasswordForm.email,
      }));
    } catch (err) {
      toast.error(getFirstError(err?.data || "Something went wrong"));
    }
  };

  const renderForgotPassword = () => (
    <form
      onSubmit={handleForgotPasswordSubmit}
      className="mx-auto w-full max-w-[292px]"
    >
      <div className="flex flex-col items-center gap-[20px]">
        <button className="flex h-[52px] w-[50px] items-center justify-center rounded-[5px] bg-gradient-to-br from-[#F5C5B8] via-[#E1A39B] to-[#A84447] shadow-sm transition hover:opacity-90 cursor-default">
          <ModalLogo />
        </button>
        <h2 className="text-center font-helvetica text-[28px] font-bold leading-none text-[#111111]">
          Reset Your Password
        </h2>
      </div>

      <p className="mx-auto mt-4 max-w-[280px] text-center font-helvetica text-[14px] leading-[18px] text-[#9B9B9B]">
        Enter your email address and we&apos;ll send you a password reset link.
      </p>

      <div className="mt-8 flex flex-col gap-3">
        <input
          type="email"
          name="email"
          placeholder="Email Address"
          value={forgotPasswordForm.email}
          onChange={handleForgotPasswordInputChange}
          className={inputClass}
          required
        />

        <button
          type="submit"
          disabled={isPasswordResetLoading}
          className="h-[40px] rounded-[5px] bg-[#313131] text-[12px] font-medium text-white transition-colors hover:bg-[#1F1F23] disabled:cursor-not-allowed disabled:bg-gray-400"
        >
          {isPasswordResetLoading ? "Sending Link..." : "Send Link"}
        </button>
      </div>

      <p className="mt-8 text-center text-[11px] text-[#9B9B9B]">
        Remembered your password?{" "}
        <button
          type="button"
          onClick={() => setMode("login")}
          className="font-medium text-[#313131]"
        >
          Back to login
        </button>
      </p>
    </form>
  );

  if (!isOpen) return null;
  if (autoStartGoogle) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden bg-black/70 px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-label={
        mode === "login"
          ? "Login modal"
          : mode === "signup"
            ? "Create account modal"
            : mode === "verifyEmail"
              ? "Verify email modal"
              : "Forgot password modal"
      }
    >
      <div className="relative flex max-h-[calc(100vh-48px)] w-full max-w-[735px] flex-col rounded-[4px] bg-white shadow-[0_20px_60px_rgba(0,0,0,0.25)]">
      {isUpdatingProfile && (
          <div
            className="absolute inset-0 z-30 flex items-center justify-center rounded-[4px] bg-white/95 px-6"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <div className="flex max-w-[320px] flex-col items-center text-center">
              <span
                className="h-10 w-10 animate-spin rounded-full border-[3px] border-[#DDD6D0] border-t-[#313131]"
                aria-hidden="true"
              />

              <h3 className="mt-5 text-[18px] font-semibold text-[#313131]">
                Updating your profile
              </h3>

              <p className="mt-2 text-[13px] leading-[19px] text-[#8A8580]">
                Please wait while we attach your survey results to your account.
                This may take a few seconds.
              </p>
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={handleCloseModal}
          disabled={isAuthenticationFlowLoading}
          aria-label="Close authentication modal"
          className="absolute right-5 top-5 z-10 flex h-8 w-8 items-center justify-center rounded-full text-[22px] leading-none text-[#777] hover:bg-[#F2F2F2] hover:text-[#111] disabled:cursor-not-allowed disabled:opacity-40"
        >
          ×
        </button>
        <div
          className={`min-h-0 flex-1 overflow-y-auto px-6 md:px-10 ${
            mode === "signup"
              ? "py-[80px]"
              : mode === "verifyEmail"
                ? "py-[70px]"
                : mode === "forgotPassword"
                  ? "py-[80px]"
                  : "py-[100px]"
          }`}
        >
          {mode === "login" && renderLogin()}
          {mode === "signup" && renderSignup()}
          {mode === "verifyEmail" && renderVerifyEmail()}
          {mode === "forgotPassword" && renderForgotPassword()}
        </div>{" "}
      </div>
    </div>,
    document.body,
  );
};

export default AuthModal;
